import React from 'react';
import ButtonSymphony from 'elements/ButtonSymphony';
import { Container } from 'modules/Sty';

interface Invoice {
  id: string;
  amountDue: number; // Already in component's expected format (e.g., 100.00)
  amountPaid: number; // Already in component's expected format
  amountRemaining: number; // Already in component's expected format
  currency: string; // e.g., USD
  status: string;
  dueDate: string; // Formatted date string
  createdDate: string; // Formatted date string
  // Add fields from API needed for actions (original names for clarity before mapping if any)
  invoice_pdf?: string; // URL for the invoice PDF
  hosted_invoice_url?: string; // URL to view on Stripe
  payment_description?: string; // Payment description from charge or payment_intent
  payment_name?: string; // Payment name from charge or payment_intent
}

interface InvoiceListProps {
  invoices: Invoice[];
}

const InvoiceList: React.FC<InvoiceListProps> = ({ invoices }) => {
  const handleViewInvoice = (hostedInvoiceUrl: string) => {
    if (!hostedInvoiceUrl) return;
    window.open(hostedInvoiceUrl, '_blank');
  };

  const handleDownloadAllPdfs = () => {
    invoices.forEach(invoice => {
      if (invoice.invoice_pdf) {
        const link = document.createElement('a');
        link.href = invoice.invoice_pdf;
        link.setAttribute('download', `invoice_${invoice.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const hasAnyPdf = invoices.some(invoice => !!invoice.invoice_pdf);

  const sty = {
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: 16,
    },
    th: {
      padding: '12px 16px',
      textAlign: 'left' as const,
      borderBottom: '2px solid #E5E7EB',
      fontWeight: 600,
      fontSize: 14,
      color: '#374151',
      backgroundColor: '#F9FAFB',
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #E5E7EB',
      fontSize: 14,
    },
    
    paymentSubtitle: {
      fontSize: 12,
      color: '#6B7280',
      fontWeight: 400,
      marginTop: 4,
      lineHeight: 1.3,
    },
    
    statusBadge: {
      padding: '4px 8px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 500,
      textTransform: 'capitalize' as const,
    },
  };

  const getStatusBadgeStyle = (status: string) => {
    const baseStyle = sty.statusBadge;
    switch (status.toLowerCase()) {
      case 'paid':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'open':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'draft':
        return { ...baseStyle, backgroundColor: '#E5E7EB', color: '#374151' };
      default:
        return { ...baseStyle, backgroundColor: '#E5E7EB', color: '#374151' };
    }
  };

  return (
    <Container>
      {/* <Container margin="0 0 16 0">
        <ButtonSymphony
          className="downloadAllInvoicesButton"
          onClick={handleDownloadAllPdfs}
          disabled={!hasAnyPdf}
          variant="outlined"
        >
          Download All Invoices
        </ButtonSymphony>
      </Container> */}
      
      <table style={sty.table}>
        <thead>
          <tr>
            <th style={sty.th}>Invoice ID</th>
            <th style={sty.th}>Amount Due</th>
            <th style={sty.th}>Amount Paid</th>
            <th style={sty.th}>Amount Remaining</th>
            <th style={sty.th}>Currency</th>
            <th style={sty.th}>Status</th>
            <th style={sty.th}>Created Date</th>
            <th style={sty.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const hasPaymentInfo = invoice.payment_name || invoice.payment_description;
            
            return (
              <tr key={invoice.id}>
                <td style={sty.td}>
                  <div>
                    {invoice.id}
                    {hasPaymentInfo && (
                      <div style={sty.paymentSubtitle}>
                        {invoice.payment_name && <div>{invoice.payment_name}</div>}
                        {invoice.payment_description && <div>{invoice.payment_description}</div>}
                      </div>
                    )}
                  </div>
                </td>
                <td style={sty.td}>{invoice.amountDue.toFixed(2)}</td>
                <td style={sty.td}>{invoice.amountPaid.toFixed(2)}</td>
                <td style={sty.td}>{invoice.amountRemaining.toFixed(2)}</td>
                <td style={sty.td}>{invoice.currency}</td>
                <td style={sty.td}>
                  <span style={getStatusBadgeStyle(invoice.status)}>
                    {invoice.status}
                  </span>
                </td>
                <td style={sty.td}>{invoice.createdDate}</td>
                <td style={sty.td}>
                  <ButtonSymphony
                    className="viewInvoiceButton"
                    onClick={() => handleViewInvoice(invoice.hosted_invoice_url!)}
                    disabled={!invoice.hosted_invoice_url}
                    variant="primary"
                    size="small"
                  >
                    View Invoice
                  </ButtonSymphony>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Container>
  );
};

export default InvoiceList;
